// Da Vinci Toolbox (Sidebar - WebtoonEditor Style)
import { DaVinciState } from "../core/DaVinciState.js";
import { DaVinciEvents } from "../core/DaVinciEvents.js";
import { DaVinciService } from "../DaVinciService.js";

export const DaVinciToolbox = {
    render() {
        const state = DaVinciState.get();
        const div = document.createElement('div');
        div.className = 'davinci-toolbox hidden-scrollbar'; // Add custom scrollbar hiding if needed
        // Style override for width
        div.style.width = '400px';
        div.style.backgroundColor = '#121212';
        div.style.borderRight = '1px solid #333';
        div.style.display = 'flex';
        div.style.flexDirection = 'column';
        div.style.overflowY = 'auto'; // Scrollable content

        div.innerHTML = `
            <div class="p-6 space-y-8">
                <!-- 1. Episode Settings Section -->
                <div class="space-y-5">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="material-symbols-outlined text-amber-500">tune</span>
                        <span class="text-sm font-bold text-gray-400 uppercase tracking-wider">${window.locale.t('davinci.episode_settings')}</span>
                    </div>

                    <!-- Title -->
                    <div class="space-y-1">
                        <label class="text-xs text-gray-500 font-bold ml-1">${window.locale.t('davinci.title')}</label>
                        <input id="tb-title" type="text" class="dv-form-input" value="${state.title || ''}" placeholder="${window.locale.t('davinci.title')}">
                    </div>

                    <!-- Thumbnail -->
                    <div class="space-y-1">
                        <label class="text-xs text-gray-500 font-bold ml-1">${window.locale.t('davinci.thumbnail')}</label>
                        <div class="flex gap-2">
                            <div class="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 flex-shrink-0 relative group cursor-pointer" id="btn-thumb-upload">
                                ${state.metadata.thumbnail
                ? `<img src="${state.metadata.thumbnail}" class="w-full h-full object-cover">`
                : `<div class="w-full h-full flex items-center justify-center text-zinc-600 group-hover:text-amber-500 transition-colors"><span class="material-symbols-outlined">add_photo_alternate</span></div>`
            }
                            </div>
                            <div class="flex-grow flex flex-col justify-center gap-2">
                                <div class="text-xs text-zinc-500">${window.locale.t('davinci.recommend_size')}</div>
                                <button class="text-xs bg-zinc-800 hover:bg-zinc-700 text-gray-300 py-1.5 px-3 rounded border border-zinc-700 transition-colors" id="btn-thumb-upload-text">
                                    ${window.locale.t('davinci.upload_image')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- BGM -->
                    <div class="space-y-1">
                        <label class="text-xs text-gray-500 font-bold ml-1">${window.locale.t('davinci.bgm')}</label>
                        <input id="tb-bgm" type="text" class="dv-form-input text-xs font-mono text-gray-400" value="${state.metadata.bgm || ''}" placeholder="${window.locale.t('davinci.bgm_placeholder')}">
                        <div id="bgm-preview" class="hidden mt-2 p-2 bg-zinc-900 rounded border border-zinc-800 flex flex-col items-center">
                            <audio id="bgm-player" controls class="w-full h-6"></audio>
                        </div>
                    </div>
                </div>

                <div class="h-px bg-zinc-800 my-4"></div>

                <!-- 2. Content Blocks Section -->
                <div class="space-y-4">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="material-symbols-outlined text-purple-500">add_circle</span>
                        <span class="text-sm font-bold text-gray-400 uppercase tracking-wider">${window.locale.t('davinci.add_block')}</span>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <button class="flex flex-col items-center justify-center gap-2 p-4 bg-zinc-800 hover:bg-zinc-700 hover:border-amber-500/50 border border-zinc-700 rounded-xl transition-all group" id="btn-add-text">
                            <span class="material-symbols-outlined text-2xl text-gray-400 group-hover:text-white">text_fields</span>
                            <span class="text-xs font-bold text-gray-400 group-hover:text-white">${window.locale.t('davinci.text_block')}</span>
                        </button>
                        <button class="flex flex-col items-center justify-center gap-2 p-4 bg-zinc-800 hover:bg-zinc-700 hover:border-amber-500/50 border border-zinc-700 rounded-xl transition-all group" id="btn-add-image">
                            <span class="material-symbols-outlined text-2xl text-gray-400 group-hover:text-white">image</span>
                            <span class="text-xs font-bold text-gray-400 group-hover:text-white">${window.locale.t('davinci.image_block')}</span>
                        </button>
                    </div>
                    
                    <div class="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                        <p class="text-[10px] text-zinc-500 leading-relaxed">
                            <span class="text-amber-500 font-bold">* ${window.locale.t('davinci.tip')}</span> ${window.locale.t('davinci.drag_tip')}
                        </p>
                    </div>
                </div>

                <div class="h-px bg-zinc-800 my-4"></div>

                <!-- 3. Publishing Actions (Moved to Header) -->
                <!-- Removed redundunt buttons as per user request -->
                <!-- <div class="space-y-4 pb-8"> ... </div> -->
            </div>
        `;

        // --- Logic Binding ---

        // 1. Title
        const titleInput = div.querySelector('#tb-title');
        titleInput.oninput = (e) => {
            DaVinciState.setTitle(e.target.value);
            // Sync with canvas title if needed (via State listener usually, but direct set works)
        };

        // 2. Thumbnail
        const uploadThumb = async () => {
            const input = document.createElement('input');
            input.type = 'file'; input.accept = 'image/*';
            input.onchange = async (e) => {
                if (e.target.files[0]) {
                    try {
                        const file = e.target.files[0];
                        window.Modal.toast(window.locale.t('davinci.thumbnail_uploading'));

                        // [FIX] Upload to Storage
                        const path = `users/${window.user.uid}/davinci/thumbnails`;
                        const res = await DaVinciService.uploadImage(file, path);

                        if (res.success) {
                            const s = DaVinciState.get();
                            s.metadata.thumbnail = res.url;
                            // Force refresh UI
                            const imgContainer = div.querySelector('#btn-thumb-upload');
                            imgContainer.innerHTML = `<img src="${res.url}" class="w-full h-full object-cover">`;
                            window.Modal.toast(window.locale.t('davinci.thumbnail_applied'));
                        } else {
                            throw new Error(res.error);
                        }
                    } catch (err) { window.Modal.alert(window.locale.t('davinci.upload_fail') + err.message); }
                }
            };
            input.click();
        };
        div.querySelector('#btn-thumb-upload').onclick = uploadThumb;
        div.querySelector('#btn-thumb-upload-text').onclick = uploadThumb;

        // 3. BGM
        const bgmInput = div.querySelector('#tb-bgm');
        const bgmPlayer = div.querySelector('#bgm-player');
        const bgmPreview = div.querySelector('#bgm-preview');

        const updateBgm = (url) => {
            const res = DaVinciService.processBgmUrl(url);
            if (res.url) {
                if (bgmInput.value !== res.url) bgmInput.value = res.url;
                bgmPlayer.src = res.url;
                bgmPreview.classList.remove('hidden');
                DaVinciState.get().metadata.bgm = res.url;
            } else {
                bgmPreview.classList.add('hidden');
                if (url && res.error) window.Modal.toast(res.error);
                DaVinciState.get().metadata.bgm = null;
            }
        };

        // Initial State Check
        if (state.metadata.bgm) updateBgm(state.metadata.bgm);

        bgmInput.onchange = (e) => updateBgm(e.target.value);

        // 4. Block Actions
        div.querySelector('#btn-add-text').onclick = () => DaVinciState.addBlock('text');
        div.querySelector('#btn-add-image').onclick = () => DaVinciState.addBlock('image');

        // 5. Publishing Actions (Moved to Header)
        // div.querySelector('#btn-save-draft').onclick = ...
        // div.querySelector('#btn-publish-now').onclick = ...

        // External State Listener (To sync if title changes from elsewhere)
        // Note: For simplicity in this step, we rely on one-way or re-render for complex sync.
        // But title sync is good.
        // We can attach a listener ID if we want to detach later, but Toolbox is usually persistent in Editor.

        // 6. Editor Guide (Button Explanation & Auto Save Status)
        import('./DaVinciEditorGuide.js').then(({ DaVinciEditorGuide }) => {
            div.appendChild(DaVinciEditorGuide.render());
        });

        return div;
    },
};
